import { CustomerFlowType } from '../../constants/customer-flow-type';
import { updateCustomerInMultiContext } from '../../helpers/launchDarkly/ldContext.helper';
import { ResolverError } from '../../constants/resolver-error';
import { CustomerSearchInput, PayPeriodTypes } from '../../gql/graphql';
import { buildAnalytics } from '../../helpers/analytics/buildAnalytics';
import { isValidAddress } from '../../helpers/validators/address.helper';
import applicationFormService from '../../services/applicationForm.service';
import { identify } from '../../state-machine/actions/segment';
import { CustomerSearchRequestMachineContext } from '../../state-machine/types/shared/context/customerSearchMachineContext';
import { CustomerSearchResult } from './queryCustomersByCellphone';

export const queryCustomers = async (
  context: CustomerSearchRequestMachineContext,
  customerCheckPayload: CustomerSearchInput,
): Promise<CustomerSearchResult> => {
  // Attach analytics (metadata moment + campaign params) to every customer query, mirroring what
  // the Apply mutation already sends. This is the single choke point all customer-search callers
  // funnel through, so buildAnalytics is the sole source of the analytics block.
  const payload: CustomerSearchInput = {
    ...customerCheckPayload,
    analytics: buildAnalytics(),
  };

  const { data, errors } = await applicationFormService.customer(payload); // TODO: move to shared code block
  // customerFlowType guaranteed to be set after this block
  var result = {
    application: data?.customer?.eligibility?.application,
    customers: data?.customer?.customers,
    customerSearchErrors: data?.customer?.customerSearchErrors,
    eligibilityDetails: data?.customer?.eligibility,
    resolverErrors: errors as any as ResolverError[], // TODO typing
    optedIntoSmsMarketing: !!data?.customer?.customers?.length && data.customer.customers.every((c) => c.smsMarketingOptIn === true),
  } as CustomerSearchResult;

  // TODO: handle errors explicitly via throw
  switch (data?.customer?.customers?.length) {
    case 0:
      result.customerFlowType = CustomerFlowType.NewCustomer;
      break;
    case 1:
      // there should be only one customer at this point after the search
      // check if customer verified by seeing if PII set in response
      if (data?.customer.customers![0].firstName) {
        const onlyCustomer = data?.customer.customers[0];
        // Only set these fields if they are not in context because the assign will overwrite if truthy

        const currentLdContext = context.ldClient.getContext();
        context.ldClient.identify(updateCustomerInMultiContext(currentLdContext, { custSorId: onlyCustomer.id! }));

        const oldBasicInfo = context.basicInfo;
        if (!oldBasicInfo || Object.values(oldBasicInfo).every((val) => !val)) {
          result.basicInfo = {
            emailAddress: onlyCustomer.emailAddress,
            firstName: onlyCustomer.firstName,
            lastName: onlyCustomer.lastName,
          };
        }

        const oldBasicInfoCont = context.basicInfoContinued;
        if (!oldBasicInfoCont || Object.entries(oldBasicInfoCont).every(([, value]) => !value)) {
          result.basicInfoContinued = {
            ...oldBasicInfoCont,
            birthDate: onlyCustomer.birthDate,
            tokenizedTaxId: onlyCustomer?.tokenizedTaxId ?? undefined,
            monthlyIncome: data?.customer?.eligibility?.application?.lease?.employment?.monthlyIncome ?? undefined,
          };
        }

        const oldPayFrequency = context.payFrequency;
        if (
          !oldPayFrequency ||
          Object.entries(oldPayFrequency)
            .filter(([key]) => key !== 'nextPayDate' && key !== 'isPaidViaDirectDeposit') // exclude nullable fields
            .every(([, value]) => !value)
        ) {
          result.payFrequency = {
            paymentFrequency: data?.customer?.eligibility?.application?.lease?.employment?.payPeriod ?? ('' as PayPeriodTypes),
            lastPayDate: data?.customer?.eligibility?.application?.lease?.employment?.lastPayDate ?? '',
            nextPayDate: data?.customer?.eligibility?.application?.lease?.employment?.nextPayDate ?? '',
            isPaidViaDirectDeposit: data?.customer?.eligibility?.application?.lease?.employment?.isPaidViaDirectDeposit ?? false,
          };
        }

        if (!isValidAddress(context.accommodationAddress)) {
          result.accommodationAddress = onlyCustomer.accommodationAddress ?? undefined;
        }

        if (!isValidAddress(context.billingAddress)) {
          result.billingAddress = onlyCustomer.billingAddress ?? onlyCustomer.accommodationAddress ?? undefined;
        }

        if (context.shouldProcessNeuroId) nid('setRegisteredUserId', onlyCustomer.id?.toString());
        identify(data.customer.customers[0].id!);

        result.customerFlowType = CustomerFlowType.VerifiedCustomer;
      } else {
        // single customer, not verified
        result.customerFlowType = CustomerFlowType.SingleCustomer;
      }
      break;
    default:
      result.customerFlowType = CustomerFlowType.MultipleCustomers;
      break;
  }
  return result;
};
